import React, { useState, useEffect } from "react";
import axios from "axios";
import { X, Radio, Loader2, Wifi } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const EMPTY = {
  nama: "",
  host: "",
  snmp_port: 161,
  snmp_community: "public",
  snmp_version: "v2c",
  vendor: "HSGQ",
  olt_type: "gpon",
  pon_ports: 8,
  mikrotik_router_id: "",
  catatan: "",
};

export const AddOltDeviceModal = ({ initialData, onClose, onSuccess }) => {
  const [form, setForm] = useState(EMPTY);
  const [vendors, setVendors] = useState([]);
  const [routers, setRouters] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [force, setForce] = useState(false);
  const isEdit = Boolean(initialData?.id);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/jaringan/olt/vendors`, { withCredentials: true })
      .then((r) => setVendors(r.data.vendors || []))
      .catch(() => setVendors([]));
    axios
      .get(`${BACKEND_URL}/api/jaringan/mikrotik/routers`, { withCredentials: true })
      .then((r) => setRouters(r.data || []))
      .catch(() => setRouters([]));
    if (initialData) {
      setForm({
        nama: initialData.nama || "",
        host: initialData.host || "",
        snmp_port: initialData.snmp_port || 161,
        snmp_community: initialData.snmp_community || "public",
        snmp_version: initialData.snmp_version || "v2c",
        vendor: (initialData.vendor || "HSGQ").toUpperCase(),
        olt_type: (initialData.olt_type || "gpon").toLowerCase(),
        pon_ports: initialData.pon_ports || 8,
        mikrotik_router_id: initialData.mikrotik_router_id || "",
        catatan: initialData.catatan || "",
      });
    }
    // eslint-disable-next-line
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { ...form, snmp_port: parseInt(form.snmp_port) || 161, pon_ports: parseInt(form.pon_ports) || 8 };
      if (isEdit) {
        await axios.patch(`${BACKEND_URL}/api/jaringan/olt/devices/${initialData.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${BACKEND_URL}/api/jaringan/olt/devices${force ? "?force=true" : ""}`, payload, { withCredentials: true });
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Gagal menyimpan OLT");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-purple-500";
  const labelCls = "text-xs font-semibold text-slate-300 block mb-1";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="add-olt-modal">
      <div className="w-full max-w-lg bg-[#0e1628] border border-slate-800 rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-slate-800 sticky top-0 bg-[#0e1628] z-10">
          <h2 className="text-sm font-bold text-white flex items-center gap-2">
            <Radio className="w-4 h-4 text-purple-400" />
            {isEdit ? "Edit OLT Fisik" : "Tambah OLT Fisik (SNMP)"}
          </h2>
          <button onClick={onClose} data-testid="add-olt-close" className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div>
            <label className={labelCls}>Nama OLT *</label>
            <input value={form.nama} onChange={(e) => set("nama", e.target.value)} required placeholder="OLT HSGQ Pusat" className={inputCls} data-testid="olt-nama-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Merk / Vendor *</label>
              <select value={form.vendor} onChange={(e) => set("vendor", e.target.value)} className={inputCls} data-testid="olt-vendor-select">
                {vendors.length > 0
                  ? vendors.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)
                  : ["HSGQ", "HIOSO", "VSOL", "CDATA", "BDCOM", "ZTE", "HUAWEI", "FIBERHOME"].map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Jenis OLT *</label>
              <div className="flex gap-2">
                {["gpon", "epon"].map((t) => (
                  <button
                    type="button"
                    key={t}
                    onClick={() => set("olt_type", t)}
                    data-testid={`olt-type-${t}`}
                    className={`flex-1 py-2 rounded-xl text-xs font-bold uppercase border transition-colors ${
                      form.olt_type === t
                        ? "bg-purple-600/30 border-purple-500 text-purple-300"
                        : "bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-600"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className={labelCls}>IP / Host OLT *</label>
              <input value={form.host} onChange={(e) => set("host", e.target.value)} required placeholder="103.x.x.x atau 10.10.100.2" className={inputCls} data-testid="olt-host-input" />
            </div>
            <div>
              <label className={labelCls}>Port SNMP</label>
              <input type="number" value={form.snmp_port} onChange={(e) => set("snmp_port", e.target.value)} className={inputCls} data-testid="olt-port-input" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className={labelCls}>SNMP Community *</label>
              <input value={form.snmp_community} onChange={(e) => set("snmp_community", e.target.value)} required placeholder="public" className={inputCls} data-testid="olt-community-input" />
            </div>
            <div>
              <label className={labelCls}>Versi SNMP</label>
              <select value={form.snmp_version} onChange={(e) => set("snmp_version", e.target.value)} className={inputCls} data-testid="olt-version-select">
                <option value="v2c">v2c</option>
                <option value="v1">v1</option>
              </select>
            </div>
          </div>

          <div>
            <label className={labelCls}>Jumlah PON Port</label>
            <input type="number" min={1} max={64} value={form.pon_ports} onChange={(e) => set("pon_ports", e.target.value)} className={inputCls} data-testid="olt-pon-input" />
          </div>

          <div>
            <label className={labelCls}>Terhubung ke Router MikroTik</label>
            <select value={form.mikrotik_router_id} onChange={(e) => set("mikrotik_router_id", e.target.value)} className={inputCls} data-testid="olt-router-select">
              <option value="">-- Tidak ditautkan --</option>
              {routers.map((r) => <option key={r.id} value={r.id}>{r.nama} ({r.host})</option>)}
            </select>
            <p className="text-[10px] text-slate-500 mt-1">OLT ini berada di belakang router mana — untuk sinkronisasi data ONU ↔ PPPoE pelanggan.</p>
          </div>

          <div>
            <label className={labelCls}>Catatan</label>
            <input value={form.catatan} onChange={(e) => set("catatan", e.target.value)} placeholder="Lokasi / keterangan" className={inputCls} data-testid="olt-catatan-input" />
          </div>

          {!isEdit && (
            <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
              <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} className="rounded" data-testid="olt-force-check" />
              Simpan paksa walau tes SNMP gagal (OLT belum terjangkau dari server)
            </label>
          )}

          {error && (
            <div className="p-3 rounded-xl bg-rose-950/40 border border-rose-800 text-rose-300 text-xs" data-testid="add-olt-error">
              {error}
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold">
              Batal
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="add-olt-submit"
              className="flex-1 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-500 hover:to-fuchsia-500 text-white text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wifi className="w-3.5 h-3.5" />}
              {saving ? "Tes SNMP & Simpan..." : isEdit ? "Simpan & Tes Ulang" : "Tes SNMP & Simpan"}
            </button>
          </div>
          <p className="text-[10px] text-slate-500">
            Server akan tes SNMP (sysDescr/sysUpTime) ke OLT sebelum menyimpan. Pastikan SNMP diaktifkan di OLT dan IP server ini diizinkan.
          </p>
        </form>
      </div>
    </div>
  );
};
